import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import Swal from "sweetalert2";
import { importPatients, fetchPatients } from "../../store/slices/patientsSlice";
import { parsePatientWorkbook, toImportPayload } from "../../utils/patientExcelImport";
import "remixicon/fonts/remixicon.css";

const PREVIEW_LIMIT = 25;

const ImportPatientsModal = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { isImporting } = useSelector((state) => state.patients);

  const [fileName, setFileName] = useState("");
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState("");
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [isParsing, setIsParsing] = useState(false);

  const reset = () => {
    setFileName("");
    setParsed(null);
    setParseError("");
    setIsParsing(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (isImporting) return;
    reset();
    toggle();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setParsed(null);
    setParseError("");
    setIsParsing(true);

    try {
      const buffer = await file.arrayBuffer();
      const result = parsePatientWorkbook(buffer);

      if (result.rows.length === 0) {
        setParseError("No patient rows were found in this sheet.");
      } else {
        setParsed(result);
      }
    } catch (err) {
      setParseError(err.message || "Could not read this file.");
    } finally {
      setIsParsing(false);
    }
  };

  const handleImport = async () => {
    if (!parsed) return;

    try {
      const response = await dispatch(
        importPatients({ rows: toImportPayload(parsed.rows), skipDuplicates })
      ).unwrap();

      await dispatch(fetchPatients());

      const skipped = response.skipped || 0;
      await Swal.fire({
        title: "Import complete",
        html: `
          <div class="text-start">
            <div><strong>${response.imported || 0}</strong> record(s) imported</div>
            ${skipped ? `<div class="text-muted">${skipped} duplicate row(s) skipped</div>` : ""}
          </div>`,
        icon: "success",
        confirmButtonText: "OK",
        position: "top",
      });

      reset();
      toggle();
    } catch (err) {
      const message =
        typeof err === "string"
          ? err
          : Object.values(err || {})
              .flat()
              .slice(0, 5)
              .join("<br/>") || "Failed to import patients.";

      Swal.fire({
        title: "Import failed",
        html: message,
        icon: "error",
        confirmButtonText: "OK",
        position: "top",
      });
    }
  };

  const previewRows = parsed ? parsed.rows.slice(0, PREVIEW_LIMIT) : [];

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl" scrollable backdrop="static">
      <ModalHeader toggle={handleClose}>
        <i className="ri-file-excel-2-line me-2 text-success"></i>
        Import Active Clients from Excel
      </ModalHeader>

      <ModalBody>
        {/* ===== File picker ===== */}
        <div className="mb-3">
          <label className="form-label fw-bold">Excel file (.xlsx, .xls, .csv)</label>
          <input
            ref={fileInputRef}
            type="file"
            className="form-control"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            disabled={isImporting}
          />
          <div className="form-text">
            The sheet must contain the <strong>Name</strong>, <strong>Age</strong> and{" "}
            <strong>Gender</strong> columns. Source of Referral, Recommended For, No. of Admission,
            Date of Admission, Date of Discharge, Length of Stay, Assigned PRP and Reason for
            Discharge are merged into the patient&apos;s <strong>Notes</strong> field. Every imported
            record is flagged as <span className="badge bg-label-info">Imported</span>.
          </div>
        </div>

        {isParsing && (
          <div className="d-flex align-items-center gap-2 text-muted">
            <span className="spinner-border spinner-border-sm" role="status"></span>
            Reading {fileName}…
          </div>
        )}

        {parseError && (
          <div className="alert alert-danger">
            <i className="ri-error-warning-line me-1"></i>
            {parseError}
          </div>
        )}

        {parsed && (
          <>
            {/* ===== Summary ===== */}
            <div className="row g-2 mb-3">
              <div className="col-md-3">
                <div className="border rounded p-2 text-center">
                  <div className="h4 mb-0">{parsed.rows.length}</div>
                  <small className="text-muted">Rows ready</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-2 text-center">
                  <div className="h4 mb-0">{parsed.duplicateCount}</div>
                  <small className="text-muted">Duplicates in file</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-2 text-center">
                  <div className="h4 mb-0">{parsed.skippedRows}</div>
                  <small className="text-muted">Rows without a name</small>
                </div>
              </div>
              <div className="col-md-3">
                <div className="border rounded p-2 text-center">
                  <div className="h6 mb-0 text-truncate" title={parsed.sheetName}>
                    {parsed.sheetName}
                  </div>
                  <small className="text-muted">Sheet</small>
                </div>
              </div>
            </div>

            {parsed.unmatchedHeaders.length > 0 && (
              <div className="alert alert-warning py-2">
                <i className="ri-information-line me-1"></i>
                These columns were not recognised and will not be imported:{" "}
                <strong>{parsed.unmatchedHeaders.join(", ")}</strong>
              </div>
            )}

            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="skipDuplicates"
                checked={skipDuplicates}
                onChange={(e) => setSkipDuplicates(e.target.checked)}
                disabled={isImporting}
              />
              <label className="form-check-label" htmlFor="skipDuplicates">
                Skip records already imported (matched on name + year)
              </label>
            </div>

            {/* ===== Preview ===== */}
            <h6 className="fw-bold">
              Preview{" "}
              <span className="text-muted fw-normal">
                (first {Math.min(PREVIEW_LIMIT, parsed.rows.length)} of {parsed.rows.length})
              </span>
            </h6>
            <div className="table-responsive border rounded" style={{ maxHeight: "320px" }}>
              <table className="table table-sm table-striped mb-0">
                <thead className="sticky-top bg-light">
                  <tr>
                    <th>Row</th>
                    <th>Year</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Primary Care Giver</th>
                    <th>Relation</th>
                    <th>Marital</th>
                    <th>Religion</th>
                    <th>Nationality</th>
                    <th>Education</th>
                    <th>Occupation</th>
                    <th>Socio-Economic</th>
                    <th>Address</th>
                    <th style={{ minWidth: "260px" }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row) => (
                    <tr key={row.row_number}>
                      <td className="text-muted">{row.row_number}</td>
                      <td>{row.record_year || "—"}</td>
                      <td>{row.first_name}</td>
                      <td>{row.last_name || "—"}</td>
                      <td>{row.age ?? "—"}</td>
                      <td className="text-capitalize">{row.gender || "—"}</td>
                      <td>{row.primary_care_giver || "—"}</td>
                      <td>{row.primary_caregiver_relation || "—"}</td>
                      <td>{row.marital_status || "—"}</td>
                      <td>{row.religion || "—"}</td>
                      <td>{row.nationality || "—"}</td>
                      <td>{row.education || "—"}</td>
                      <td>{row.occupation || "—"}</td>
                      <td>{row.socio_economic_status || "—"}</td>
                      <td>{row.address || "—"}</td>
                      <td>
                        {row.notes ? (
                          <pre
                            className="mb-0 small"
                            style={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}
                          >
                            {row.notes}
                          </pre>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ===== Actions ===== */}
        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={handleClose}
            disabled={isImporting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleImport}
            disabled={!parsed || isImporting}
          >
            {isImporting ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Importing…
              </>
            ) : (
              <>
                <i className="ri-upload-2-line me-1"></i>
                Import {parsed ? `${parsed.rows.length} record(s)` : ""}
              </>
            )}
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ImportPatientsModal;
